import type { ErrorEvent, Event } from '@sentry/node'

import type { ErrorReportController } from '@app/services/ErrorReportController'

import { ApiError } from '@app/packages/api/errors'
import { ApiError as InternalApiError, isNetworkError as isInternalApiError } from '@app/packages/internalApi/errors'

import { isChunkLoadError } from './chunkLoadError'

interface EventProcessor {
  <T extends Event | ErrorEvent>(event: T, hint: { originalException?: unknown }): Promise<T | null>
  id?: string
}

/**
 * `dropFrameless` discards events whose stack has no usable frames — browser
 * extensions and inline scripts. Server stacks are always ours, so it stays off there.
 */
export const createEventProcessor =
  ({ dropFrameless, errorReportController }: { dropFrameless: boolean; errorReportController: ErrorReportController }): EventProcessor =>
  async (event, hint) => {
    try {
      try {
        const debug = window['__debug_sentry_event']
        if (debug && debug(event, hint)) return null
      } catch {}

      // A chunk that will not load is a document older than the deploy, and
      // `installChunkLoadErrorReload` is already reloading the page as this is being sent — the
      // flush before that reload is the only reason the event exists at all. Reporting the fix
      // working raises an alert per deploy for a condition that resolves in under a second.
      if (isChunkLoadError(hint.originalException)) return null

      if (dropFrameless) {
        const frames = event.exception?.values?.at(0)?.stacktrace?.frames ?? []
        if (!frames.length || frames.at(0)?.filename === '<anonymous>' || isOnlySdkFrames(frames)) {
          return null
        }
      }

      const err = errorReportController.errorForCapture(hint.originalException)
      if (!err) return null

      applyChainedTitle(event, hint.originalException)
      applyUpstreamStatusTag(event, hint.originalException)

      event.extra = event.extra || {}
      event.extra.error_data = getErrorsExtra(err)
    } catch {
      event.tags = { ...event.tags, processing_failed: true }
    }

    return event
  }

/**
 * A stack that is nothing but the SDK's own wrapper says only that something threw inside a
 * listener we instrumented — the throwing code was cross-origin, so the browser named none of it.
 * Yandex Browser on iOS reports its injected frame bridge this way, hundreds of times a month
 * (FRONTEND-84X), and there is nothing in it we could act on.
 */
function isOnlySdkFrames(frames: { filename?: string }[]) {
  return frames.every(frame => !!frame.filename && frame.filename.includes('/@sentry/'))
}

/**
 * Sentry titles an issue from the last entry of `exception.values` — our outermost
 * wrapper — so a chained error shows the operation and hides the reason. Append the
 * root cause to that entry so the title, and every alert built on it, carries both.
 */
function applyChainedTitle(event: Event | ErrorEvent, err: unknown) {
  const outermost = event.exception?.values?.at(-1)
  if (!outermost?.value) return
  const root = getCauses(err).at(-1)
  if (!(root instanceof Error)) return
  const cause = root.message.trim()
  if (!cause || outermost.value.includes(cause)) return
  outermost.value = `${outermost.value} <- ${cause.slice(0, MAX_CAUSE_LENGTH)}`
}

/**
 * An upstream 5xx is never a frontend bug, but it still surfaces here as a wrapped error.
 * Tag it with the status the API actually returned so the Slack alert rule can filter it
 * out while the issue stays in Sentry, searchable as `upstream_status:503`. A 5xx from the
 * internal api is our own server, so it is tagged `internal_status` instead — filtering it
 * out with the upstream ones would hide a bug of ours.
 */
function applyUpstreamStatusTag(event: Event | ErrorEvent, err: unknown) {
  const carrier = [err, ...getCauses(err)].reverse().find(e => {
    const status = serverStatusOf(e)
    return status !== null
  })
  if (!carrier) return
  const tag = isInternalApiError(carrier) ? 'internal_status' : 'upstream_status'
  event.tags = { ...event.tags, [tag]: String(serverStatusOf(carrier)) }
}

function serverStatusOf(e: unknown) {
  if (!e || typeof e !== 'object' || !('status' in e) || typeof e.status !== 'number') return null
  return e.status >= 500 && e.status < 600 ? e.status : null
}

function getErrorExtra(err: unknown) {
  if (!(err instanceof Error)) {
    return { type: 'unknown object' }
  }
  if (err instanceof ApiError || err instanceof InternalApiError) {
    return { type: err.name, message: err.message, response: err.response, status: err.status }
  }
  return { message: err.message }
}

function getErrorsExtra(err: unknown) {
  const root: any = getErrorExtra(err)
  const causes = getCauses(err)
  if (causes.length) {
    root.causes = causes.map(getErrorExtra)
  }
  return root
}

function getCauses(err: unknown) {
  const causes: unknown[] = []
  while (err instanceof Error && err.cause) {
    causes.push(err.cause)
    err = err.cause
  }
  return causes
}

const MAX_CAUSE_LENGTH = 200
